import { AsyncStatus } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import { handleActions } from 'redux-actions';

import { actions } from '../actions/audio';

type Audio = {
  created_at: string | null;
  created_by: User | null;
  pretty_name: string | null;
  resource_type: string | null;
  url: string | null;
};

export function emptyAudio(): Audio {
  return {
    created_at: null,
    pretty_name: null,
    created_by: null,
    resource_type: null,
    url: null,
  };
}

export type AudioState = {
  meta: {
    uploadAsyncStatus: AsyncStatus;
    getAsyncStatus: AsyncStatus;
  };
  ui: {
    isNewAudioOpen: boolean;
  };
  allAudios: Array<Audio>;
  selectedAudio: Audio;
};

const initialState: AudioState = {
  meta: {
    uploadAsyncStatus: 'succeeded',
    getAsyncStatus: 'succeeded',
  },
  ui: {
    isNewAudioOpen: false,
  },
  allAudios: [],
  selectedAudio: emptyAudio(),
};

// Destructure the audio actions.
export const {
  audioGetAll,
  audioGetAllSucceeded,
  audioGetAllFailed,
  audioGetPresignedUrl,
  audioGetPresignedUrlSucceeded,
  audioGetPresignedUrlFailed,
  audioUpload,
  audioUploadSucceeded,
  audioUploadFailed,
  audioSelect,
  audioReset,
  audioShowModal,
  audioHideModal,
} = actions;

const audio = handleActions<AudioState, any>(
  {
    [audioGetAll.toString()]: (state: AudioState, action) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'loading' } };
    },

    [audioReset.toString()]: (state: AudioState, action) => {
      return { ...state, selectedAudio: emptyAudio() };
    },

    [audioGetAllSucceeded.toString()]: (state: AudioState, action) => {
      // Update selectedAudio.
      let selectedAudio = Object.assign({}, state.selectedAudio);
      if (selectedAudio.url !== null) {
        // Update the selectedAudio with the whole object (if known yet).
        for (const audio of action.payload.data.uploads) {
          if (audio.url === selectedAudio.url) {
            selectedAudio = audio;
            break;
          }
        }
      }

      return {
        ...state,
        meta: { ...state.meta, getAsyncStatus: 'succeeded' },
        allAudios: action.payload.data.uploads,
        selectedAudio: selectedAudio,
      };
    },

    [audioGetAllFailed.toString()]: (state: AudioState, action) => {
      return { ...state, meta: { ...state.meta, getAsyncStatus: 'failed' } };
    },

    [audioGetPresignedUrl.toString()]: (state: AudioState, action) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [audioGetPresignedUrlSucceeded.toString()]: (state: AudioState, action) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
      };
    },

    [audioGetPresignedUrlFailed.toString()]: (state: AudioState, action) => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [audioUpload.toString()]: (state: AudioState, action) => {
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'loading' },
      };
    },

    [audioUploadSucceeded.toString()]: (state: AudioState, action) => {
      const selectedAudio = {
        created_at: null,
        pretty_name: action.payload.filename,
        created_by: action.payload.created_by,
        resource_type: null,
        url: action.payload.url,
      };
      return {
        ...state,
        meta: { ...state.meta, uploadAsyncStatus: 'succeeded' },
        ui: { ...state.ui, isNewAudioOpen: false },
        selectedAudio: selectedAudio,
        allAudios: [selectedAudio, ...state.allAudios],
      };
    },

    [audioUploadFailed.toString()]: (state: AudioState, action) => {
      return { ...state, meta: { ...state.meta, uploadAsyncStatus: 'failed' } };
    },

    [audioSelect.toString()]: (state: AudioState, action) => {
      let selectedAudio = Object.assign({}, action.payload.audio);

      // Update the selectedAudio with the whole object (if known yet).
      for (const audio of state.allAudios) {
        if (audio.url === action.payload.audio.url) {
          selectedAudio = audio;
          break;
        }
      }

      return { ...state, selectedAudio: selectedAudio };
    },

    [audioShowModal.toString()]: (state: AudioState, action) => {
      return { ...state, ui: { ...state.ui, isNewAudioOpen: true } };
    },

    [audioHideModal.toString()]: (state: AudioState, action) => {
      return { ...state, ui: { ...state.ui, isNewAudioOpen: false } };
    },
  },
  initialState,
);

export default audio;
